/**
 * RelatieForm - Nieuwe/Bewerk Relatie
 * EXACT zoals Morris relatie_form.html
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BoekhoudingLayout from '../../components/boekhouding/BoekhoudingLayout';
import styles from '../../styles/Boekhouding.module.css';
import { boekhoudingService, RelatieType, RelatieCreate } from '../../services/boekhouding.service';

const RelatieForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = id && id !== 'nieuw';

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  // Form state - EXACT Morris velden
  const [naam, setNaam] = useState('');
  const [type, setType] = useState<RelatieType>(RelatieType.DEBITEUR);
  const [adres, setAdres] = useState('');
  const [postcode, setPostcode] = useState('');
  const [plaats, setPlaats] = useState('');
  const [email, setEmail] = useState('');
  const [telefoon, setTelefoon] = useState('');
  const [btwNummer, setBtwNummer] = useState('');
  const [kvkNummer, setKvkNummer] = useState('');

  useEffect(() => {
    if (isEdit && id) {
      const loadRelatie = async () => {
        try {
          const relatie = await boekhoudingService.getRelatie(parseInt(id));
          setNaam(relatie.naam);
          setType(relatie.type);
          setAdres(relatie.adres || '');
          setPostcode(relatie.postcode || '');
          setPlaats(relatie.plaats || '');
          setEmail(relatie.email || '');
          setTelefoon(relatie.telefoon || '');
          setBtwNummer(relatie.btw_nummer || '');
          setKvkNummer(relatie.kvk_nummer || '');
        } catch (err) {
          console.error('Error loading relatie:', err);
          alert('Kon relatie niet laden');
          navigate('/boekhouding/relaties');
        } finally {
          setLoading(false);
        }
      };
      loadRelatie();
    }
  }, [id, isEdit, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!naam.trim()) {
      alert('Naam is verplicht');
      return;
    }

    setSaving(true);

    const data: RelatieCreate = {
      naam: naam.trim(),
      type,
      adres: adres.trim() || undefined,
      postcode: postcode.trim() || undefined,
      plaats: plaats.trim() || undefined,
      email: email.trim() || undefined,
      telefoon: telefoon.trim() || undefined,
      btw_nummer: btwNummer.trim() || undefined,
      kvk_nummer: kvkNummer.trim() || undefined,
    };

    try {
      if (isEdit && id) {
        await boekhoudingService.updateRelatie(parseInt(id), data);
      } else {
        await boekhoudingService.createRelatie(data);
      }
      navigate('/boekhouding/relaties');
    } catch (err) {
      console.error('Error saving relatie:', err);
      alert('Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  const title = isEdit ? 'Relatie Bewerken' : 'Nieuwe Relatie';

  if (loading) {
    return (
      <BoekhoudingLayout title={title}>
        <div className={styles.loading}>Laden...</div>
      </BoekhoudingLayout>
    );
  }

  return (
    <BoekhoudingLayout title={title}>
      <div className={styles.card} style={{ maxWidth: '700px', margin: '0 auto' }}>
        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Naam en Type - EXACT Morris */}
          <div className={styles.formRow}>
            <div className={`${styles.formGroup} ${styles.formGroupLarge}`}>
              <label htmlFor="naam">Naam *</label>
              <input
                type="text"
                id="naam"
                value={naam}
                onChange={(e) => setNaam(e.target.value)}
                required
                placeholder="Bedrijfsnaam of naam"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="type">Type *</label>
              <select
                id="type"
                value={type}
                onChange={(e) => setType(e.target.value as RelatieType)}
                required
              >
                <option value={RelatieType.DEBITEUR}>Debiteur (klant)</option>
                <option value={RelatieType.CREDITEUR}>Crediteur (leverancier)</option>
                <option value={RelatieType.BEIDE}>Beide</option>
              </select>
            </div>
          </div>

          {/* Adres - EXACT Morris */}
          <div className={styles.formGroup}>
            <label htmlFor="adres">Adres</label>
            <input
              type="text"
              id="adres"
              value={adres}
              onChange={(e) => setAdres(e.target.value)}
              placeholder="Straatnaam en huisnummer"
            />
          </div>

          {/* Postcode en Plaats - EXACT Morris */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="postcode">Postcode</label>
              <input
                type="text"
                id="postcode"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                placeholder="1234 AB"
              />
            </div>

            <div className={`${styles.formGroup} ${styles.formGroupLarge}`}>
              <label htmlFor="plaats">Plaats</label>
              <input
                type="text"
                id="plaats"
                value={plaats}
                onChange={(e) => setPlaats(e.target.value)}
                placeholder="Plaatsnaam"
              />
            </div>
          </div>

          {/* Email en Telefoon - EXACT Morris */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="info@voorbeeld.nl"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="telefoon">Telefoon</label>
              <input
                type="tel"
                id="telefoon"
                value={telefoon}
                onChange={(e) => setTelefoon(e.target.value)}
                placeholder="06-12345678"
              />
            </div>
          </div>

          {/* BTW en KVK - EXACT Morris */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label htmlFor="btw_nummer">BTW nummer</label>
              <input
                type="text"
                id="btw_nummer"
                value={btwNummer}
                onChange={(e) => setBtwNummer(e.target.value)}
                placeholder="NL000000000B00"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="kvk_nummer">KVK nummer</label>
              <input
                type="text"
                id="kvk_nummer"
                value={kvkNummer}
                onChange={(e) => setKvkNummer(e.target.value)}
                placeholder="12345678"
              />
            </div>
          </div>

          {/* Acties - EXACT Morris */}
          <div className={styles.formActions}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSecondary}`}
              onClick={() => navigate('/boekhouding/relaties')}
            >
              Annuleren
            </button>
            <button
              type="submit"
              className={`${styles.btn} ${styles.btnPrimary}`}
              disabled={saving}
            >
              {saving ? 'Opslaan...' : 'Opslaan'}
            </button>
          </div>
        </form>
      </div>
    </BoekhoudingLayout>
  );
};

export default RelatieForm;
